import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { explainFunctionError, functionErrorResponse, useSupabase, useTenant } from "@how/app-kit";
import { z } from "zod";
import { productSchema, type ShopProduct } from "../shop/shop-queries";
import { DISPLAY_LOCALE } from "@how/ui";

function useTenantId(): string | null {
  const tenant = useTenant();
  return tenant.status === "resolved" ? tenant.tenant.tenantId : null;
}

/** A date and time in the reader's locale; the occurrence is a wall-clock time at the campus (ADR 0036). */
export function formatWhen(iso: string, timeZone?: string | null): string {
  return new Date(iso).toLocaleString(DISPLAY_LOCALE, {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
    ...(timeZone ? { timeZone } : {}),
  });
}

const mineSchema = z.object({
  registrationId: z.string(),
  personId: z.string(),
  status: z.enum(["registered", "waitlisted", "cancelled", "attended"]),
  name: z.string(),
  isMe: z.boolean(),
  paymentStatus: z
    .enum(["not_required", "pending", "paid", "failed", "refunded"])
    .default("not_required"),
  /** Add-ons I chose for this registration (ADR 0047 step 3), not cancelled. */
  addOns: z
    .array(
      z.object({
        orderId: z.string(),
        status: z.enum(["pending_payment", "paid", "fulfilled", "cancelled"]),
        totalCents: z.number(),
        items: z.string().nullable(),
      }),
    )
    .default([]),
});

const occurrenceSchema = z.object({
  occurrenceId: z.string(),
  startsAt: z.string(),
  endsAt: z.string(),
  note: z.string().nullable(),
  isCancelled: z.boolean(),
  event: z.object({
    id: z.string(),
    title: z.string(),
    slug: z.string(),
    description: z.string().nullable(),
    ministry: z.string().nullable(),
    location: z.string().nullable(),
    category: z.string().nullable(),
    campus: z
      .object({ id: z.string(), name: z.string(), timeZone: z.string().nullable() })
      .nullable(),
    visibility: z.enum(["public", "unlisted", "private"]),
    registration: z.enum(["none", "open", "closed"]),
    capacity: z.number().nullable(),
    priceCents: z.number().nullable().default(null),
    currency: z.string().default("usd"),
    /** How many add-ons the event offers; zero without the store. */
    addOns: z.number().default(0),
  }),
  registered: z.number(),
  mine: z.array(mineSchema),
});
export type UpcomingOccurrence = z.infer<typeof occurrenceSchema>;
export type MyRegistration = z.infer<typeof mineSchema>;

const householdPersonSchema = z.object({
  personId: z.string(),
  name: z.string(),
  role: z.enum(["adult", "child", "other"]),
  isMe: z.boolean(),
});
export type HouseholdPerson = z.infer<typeof householdPersonSchema>;

/** What is on for the next two months, with where the member and their household stand on each date. */
export function useUpcomingEvents() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["upcoming-events", tenantId],
    enabled: tenantId !== null,
    retry: false,
    queryFn: async (): Promise<UpcomingOccurrence[]> => {
      const { data, error } = await supabase.rpc("upcoming_events", {
        p_tenant_id: tenantId ?? "",
        p_days: 62,
      });
      if (error) throw error;
      return z.array(occurrenceSchema).parse(data);
    },
  });
}

export function useMyHousehold() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["my-household-people", tenantId],
    enabled: tenantId !== null,
    retry: false,
    queryFn: async (): Promise<HouseholdPerson[]> => {
      const { data, error } = await supabase.rpc("my_household_people", {
        p_tenant_id: tenantId ?? "",
      });
      if (error) throw error;
      return z.array(householdPersonSchema).parse(data);
    },
  });
}

function useInvalidation() {
  const client = useQueryClient();
  const tenantId = useTenantId();
  return () => client.invalidateQueries({ queryKey: ["upcoming-events", tenantId] });
}

export function useRegisterMe() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const invalidate = useInvalidation();
  return useMutation({
    mutationFn: async (input: {
      occurrenceId: string;
      personIds: readonly string[];
      note: string;
    }) => {
      const results: { personId: string; status: string }[] = [];
      for (const personId of input.personIds) {
        const { error } = await supabase.rpc("register_me", {
          p_tenant_id: tenantId ?? "",
          p_occurrence_id: input.occurrenceId,
          p_person_id: personId,
          ...(input.note.trim() === "" ? {} : { p_note: input.note }),
        });
        if (error) throw error;
        results.push({ personId, status: "done" });
      }
      return results;
    },
    onSuccess: invalidate,
  });
}

export function useCancelMyRegistration() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const invalidate = useInvalidation();
  return useMutation({
    mutationFn: async (registrationId: string) => {
      const { error } = await supabase.rpc("cancel_my_registration", {
        p_tenant_id: tenantId ?? "",
        p_registration_id: registrationId,
      });
      if (error) throw error;
    },
    onSuccess: invalidate,
  });
}

/**
 * Pay for a registration (ADR 0009): the stripe-checkout function opens a Checkout session on the church's own
 * Stripe account for the database's amount and answers with its address; the browser goes there and comes back to
 * /events?paid=yes or no. The webhook marks the registration paid.
 */
export function usePayForRegistration() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useMutation({
    mutationFn: async (registrationId: string): Promise<string> => {
      const result = await supabase.functions.invoke<{ url: string }>("stripe-checkout", {
        body: { tenant_id: tenantId ?? "", registration_id: registrationId },
      });
      if (result.error) {
        throw await explainFunctionError(result.error, functionErrorResponse(result.error));
      }
      return result.data?.url ?? "";
    },
    onSuccess: (url) => {
      if (url !== "") window.location.assign(url);
    },
  });
}

/** A price as the member reads it. */
export function formatPrice(cents: number, currency: string): string {
  return new Intl.NumberFormat(DISPLAY_LOCALE, {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(cents / 100);
}

// ------------------------------------------------------------------------------------------------ add-ons

/** What an event offers as add-ons (ADR 0047 step 3), as the Shop shows products. */
export function useEventAddOns(eventId: string | null) {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["event-add-ons", tenantId, eventId],
    enabled: eventId !== null && tenantId !== null,
    queryFn: async (): Promise<ShopProduct[]> => {
      const { data, error } = await supabase.rpc("event_add_ons_for", {
        p_tenant_id: tenantId ?? "",
        p_event_id: eventId ?? "",
      });
      if (error) throw error;
      return z.array(productSchema).parse(data);
    },
  });
}

async function openOrderCheckout(
  supabase: ReturnType<typeof useSupabase>,
  tenantId: string,
  orderId: string,
): Promise<string> {
  const result = await supabase.functions.invoke<{ url: string }>("stripe-checkout", {
    body: { tenant_id: tenantId, order_id: orderId },
  });
  if (result.error) {
    throw await explainFunctionError(result.error, functionErrorResponse(result.error));
  }
  return result.data?.url ?? "";
}

/**
 * Add-ons for a registration: the database makes them an order linked to it, with prices from the catalog and stock
 * held. When the place itself is still to be paid, nothing more happens here: the place's Pay button now charges both
 * in one checkout. Otherwise the add-ons are paid now, on Stripe's page.
 */
export function useAddAddOns() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const invalidate = useInvalidation();
  return useMutation({
    mutationFn: async (input: {
      readonly registrationId: string;
      readonly lines: readonly { readonly variantId: string; readonly quantity: number }[];
      readonly payNow: boolean;
    }): Promise<string> => {
      const { data, error } = await supabase.rpc("add_event_add_ons", {
        p_tenant_id: tenantId ?? "",
        p_registration_id: input.registrationId,
        p_items: input.lines.map((l) => ({ variantId: l.variantId, quantity: l.quantity })),
      });
      if (error) throw new Error(error.message);
      await invalidate();
      return input.payNow ? openOrderCheckout(supabase, tenantId ?? "", data) : "";
    },
    onSuccess: (url) => {
      if (url !== "") window.location.assign(url);
    },
  });
}

/** Pay for add-ons chosen earlier, on their own (the place is paid or free). */
export function usePayForAddOns() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useMutation({
    mutationFn: (orderId: string) => openOrderCheckout(supabase, tenantId ?? "", orderId),
    onSuccess: (url) => {
      if (url !== "") window.location.assign(url);
    },
  });
}

/** Drop add-ons not paid yet; their stock goes back. */
export function useCancelAddOns() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const invalidate = useInvalidation();
  return useMutation({
    mutationFn: async (orderId: string) => {
      const { error } = await supabase.rpc("cancel_my_order", {
        p_tenant_id: tenantId ?? "",
        p_order_id: orderId,
      });
      if (error) throw new Error(error.message);
    },
    onSuccess: invalidate,
  });
}
